import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Archive,
  ArrowClockwise,
  ArrowBendUpLeft,
  ArrowBendUpRight,
  DotsThree,
  EnvelopeOpen,
  EnvelopeSimple,
  Folder,
  MagnifyingGlass,
  PaperPlaneTilt,
  Star,
  Trash,
  UserPlus,
  Warning,
} from "@phosphor-icons/react";
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";
import {
  mail as mailApi,
} from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  ActaActionMenu,
  ActaActionMenuItem,
  ActaActionSheet,
} from "@/components/ui/ActaActionMenu";
import ActaConfirmDialog from "@/components/ActaConfirmDialog";
import {
  MailComposerForm,
  MailContactAssociationDialog,
  UnknownMailContactDialog,
} from "@/components/mail/MailDialogs";
import MailAddressBookDialog from "@/components/mail/MailAddressBookDialog";
import MailAttachmentSaveDialog from "@/components/mail/MailAttachmentSaveDialog";
import MailFolderCreateDialog from "@/components/mail/MailFolderCreateDialog";
import { useAuth } from "@/context/AuthContext";
import { hasModuleAccess } from "@/lib/accessPermissions";


const EMPTY_DRAFT = {
  to: "",
  cc: "",
  bcc: "",
  subject: "",
  body: "",
  prospectId: "",
  inReplyTo: "",
  references: "",
  sendRequestId: "",
  scheduleRequestId: "",
  files: [],
};


function senderLabel(item) {
  if (
    item.direction
    === "outbound"
  ) {
    return (
      (item.to || [])
        .map(
          (entry) =>
            entry.email
        )
        .join(", ")
      || "Destinataire"
    );
  }

  return (
    item.fromName
    || item.fromEmail
    || "Expéditeur"
  );
}


function formatDate(value) {
  if (!value) return "—";

  const date = new Date(
    value
  );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "—";
  }

  return date.toLocaleString(
    "fr-FR",
    {
      dateStyle: "short",
      timeStyle: "short",
    }
  );
}


export default function Mail({ messageActions = [] }) {
  const navigate =
    useNavigate();

  const { user } =
    useAuth();

  const canAssociateContact =
    hasModuleAccess(
      user,
      "commercial"
    );

  const canSaveAttachmentsToDocuments =
    hasModuleAccess(
      user,
      "documents"
    );

  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();

  const [
    accountState,
    setAccountState,
  ] = useState({
    loading: true,
    configured: false,
    account: null,
  });

  const folder =
    searchParams.get(
      "folder"
    ) || "inbox";

  const [
    page,
    setPage,
  ] = useState(1);

  const [
    pageState,
    setPageState,
  ] = useState({
    total: 0,
    totalPages: 1,
  });

  const [
    syncWarning,
    setSyncWarning,
  ] = useState("");

  const folderId =
    searchParams.get(
      "id"
    ) || "";

  const setFolder =
    useCallback(
      (
        nextFolder,
        nextFolderId = ""
      ) => {
        const params =
          new URLSearchParams(
            searchParams
          );

        params.set(
          "folder",
          nextFolder
        );

        if (
          nextFolder === "custom"
          && nextFolderId
        ) {
          params.set(
            "id",
            nextFolderId
          );
        } else {
          params.delete("id");
        }

        params.delete("compose");
        params.delete("createFolder");
        params.delete("message");

        setPage(1);

        setSearchParams(
          params
        );
      },
      [
        searchParams,
        setSearchParams,
      ]
    );

  const [
    messages,
    setMessages,
  ] = useState([]);

  const [
    mailFolders,
    setMailFolders,
  ] = useState([]);

  const [
    selected,
    setSelected,
  ] = useState(null);

  const [
    mobileActionsOpen,
    setMobileActionsOpen,
  ] = useState(false);

  const [
    messageActionsOpen,
    setMessageActionsOpen,
  ] = useState(false);

  const messageActionsTriggerRef =
    useRef(null);

  const visibleMessageActions =
    useMemo(
      () => (
        Array.isArray(messageActions)
          ? messageActions.filter(
            (action) => (
              action
              && action.label
              && typeof action.onClick === "function"
            )
          )
          : []
      ),
      [messageActions]
    );

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    syncing,
    setSyncing,
  ] = useState(false);

  const [
    lastSyncAt,
    setLastSyncAt,
  ] = useState(null);

  const syncInProgressRef =
    useRef(false);

  const [
    search,
    setSearch,
  ] = useState("");

  const searchRef =
    useRef(search);
  searchRef.current = search;

  const [
    composeOpen,
    setComposeOpen,
  ] = useState(false);

  const [
    draft,
    setDraft,
  ] = useState(
    EMPTY_DRAFT
  );

  const [
    sending,
    setSending,
  ] = useState(false);

  const [
    archiving,
    setArchiving,
  ] = useState(false);

  const [
    restoring,
    setRestoring,
  ] = useState(false);

  const [
    deleteConfirmOpen,
    setDeleteConfirmOpen,
  ] = useState(false);

  const [
    deletingPermanently,
    setDeletingPermanently,
  ] = useState(false);

  const [
    folderCreateOpen,
    setFolderCreateOpen,
  ] = useState(false);

  const [
    folderCreating,
    setFolderCreating,
  ] = useState(false);

  const [
    folderRenameOpen,
    setFolderRenameOpen,
  ] = useState(false);

  const [
    folderRenaming,
    setFolderRenaming,
  ] = useState(false);

  const [
    folderDeleteConfirmOpen,
    setFolderDeleteConfirmOpen,
  ] = useState(false);

  const [
    folderDeleting,
    setFolderDeleting,
  ] = useState(false);

  const [
    unknownContact,
    setUnknownContact,
  ] = useState(null);

  const [
    contactAssociationOpen,
    setContactAssociationOpen,
  ] = useState(false);

  const [
    contactAssociationSaving,
    setContactAssociationSaving,
  ] = useState(false);

  const [
    attachmentSaveTarget,
    setAttachmentSaveTarget,
  ] = useState(null);

  const [
    addressBookOpen,
    setAddressBookOpen,
  ] = useState(false);

  const requestedMessageId =
    searchParams.get(
      "message"
    ) || "";

  const loadAccount =
    useCallback(
      async () => {
        try {
          const response =
            await mailApi.account();

          setAccountState({
            loading: false,
            configured:
              Boolean(
                response?.configured
              ),
            account:
              response?.account
              || null,
          });

          if (
            response?.account
          ) {
            setLastSyncAt(
              response.account
                .lastSyncAt
              || null
            );


          }
        } catch (error) {
          setAccountState({
            loading: false,
            configured: false,
            account: null,
          });

          toast.error(
            error?.response
              ?.data?.detail
            || (
              "Impossible de "
              + "charger la "
              + "configuration mail"
            )
          );
        }
      },
      []
    );

  const loadFolders =
    useCallback(
      async () => {
        if (
          !accountState
            .configured
        ) {
          setMailFolders([]);
          return;
        }

        try {
          const response =
            await mailApi
              .listFolders();

          setMailFolders(
            response?.items
            || []
          );
        } catch (error) {
          toast.error(
            error?.response
              ?.data?.detail
            || "Impossible de charger les dossiers"
          );
        }
      },
      [
        accountState
          .configured,
      ]
    );

  const loadMessages =
    useCallback(
      async () => {
        if (
          !accountState
            .configured
        ) {
          return;
        }

        setLoading(true);

        try {
          const response =
            await mailApi
              .listMessages({
                folder,
                folderId,
                page,
                limit: 50,
                search,
              });

          const totalPages =
            Math.max(
              1,
              Number(
                response
                  ?.totalPages
                || 1
              )
            );

          if (page > totalPages) {
            setPage(totalPages);
            return;
          }

          setMessages(
            response?.items
            || []
          );

          setPageState({
            total: Number(
              response?.total
              || 0
            ),
            totalPages,
          });
        } catch (error) {
          toast.error(
            error?.response
              ?.data?.detail
            || (
              "Impossible de "
              + "charger les mails"
            )
          );
        } finally {
          setLoading(false);
        }
      },
      [
        accountState
          .configured,
        folder,
        folderId,
        page,
        search,
      ]
    );

  const syncInbox =
    useCallback(
      async ({
        showError = true,
      } = {}) => {
        if (
          !accountState
            .configured
          || folder
            !== "inbox"
          || syncInProgressRef
            .current
        ) {
          return;
        }

        syncInProgressRef
          .current = true;
        setSyncing(true);

        try {
          const result =
            await mailApi.sync();

          setSyncWarning("");

          if (
            result?.lastSyncAt
          ) {
            setLastSyncAt(
              result.lastSyncAt
            );
          }

          const response =
            await mailApi
              .listMessages({
                folder: "inbox",
                folderId: "",
                page: 1,
                limit: 50,
                search:
                  searchRef.current,
              });

          setMessages(
            response?.items
            || []
          );

          setPage(1);
          setPageState({
            total: Number(
              response?.total
              || 0
            ),
            totalPages: Math.max(
              1,
              Number(
                response
                  ?.totalPages
                || 1
              )
            ),
          });
        } catch (error) {
          setSyncWarning(
            "Les nouveaux messages n’ont pas pu être récupérés. "
            + "La dernière version disponible reste consultable."
          );

          if (showError) {
            toast.error(
              error?.response
                ?.data?.detail
              || (
                "Synchronisation "
                + "mail impossible"
              )
            );
          }
        } finally {
          syncInProgressRef
            .current = false;
          setSyncing(false);
        }
      },
      [
        accountState
          .configured,
        folder,
      ]
    );

  useEffect(
    () => {
      loadAccount();
    },
    [loadAccount]
  );

  useEffect(
    () => {
      loadFolders();
    },
    [loadFolders]
  );

  useEffect(
    () => {
      loadMessages();
    },
    [loadMessages]
  );

  useEffect(
    () => {
      syncInbox();
    },
    [syncInbox]
  );

  useEffect(
    () => {
      setSelected(null);
    },
    [
      folder,
      folderId,
      page,
    ]
  );

  useEffect(
    () => {
      if (
        !accountState
          .configured
        || folder
          !== "inbox"
      ) {
        return undefined;
      }

      const timer =
        window.setInterval(
          () => {
            syncInbox({
              showError: false,
            });
          },
          60000
        );

      return () =>
        window.clearInterval(
          timer
        );
    },
    [
      accountState
        .configured,
      folder,
      syncInbox,
    ]
  );

  useEffect(
    () => {
      if (
        !requestedMessageId
        || !accountState
          .configured
      ) {
        return;
      }

      mailApi
        .getMessage(
          requestedMessageId
        )
        .then(
          (message) => {
            setSelected(
              message
            );

            if (
              !message.isRead
            ) {
              mailApi
                .updateMessage(
                  message.id,
                  {
                    is_read:
                      true,
                  }
                )
                .catch(
                  () => {}
                );
            }
          }
        )
        .catch(
          () => {}
        );
    },
    [
      requestedMessageId,
      accountState
        .configured,
    ]
  );

  useEffect(
    () => {
      const compose =
        searchParams.get(
          "compose"
        ) === "1";

      const restoreDraft =
        searchParams.get(
          "restoreDraft"
        ) === "1";

      if (restoreDraft) {
        try {
          const stored =
            window
              .sessionStorage
              .getItem(
                "acta-mail-draft"
              );

          if (stored) {
            setDraft({
              ...EMPTY_DRAFT,
              ...JSON.parse(
                stored
              ),
              files: [],
            });

            setComposeOpen(
              true
            );

            window
              .sessionStorage
              .removeItem(
                "acta-mail-draft"
              );
          }
        } catch {
          // Brouillon local
          // invalide : ignoré.
        }
      } else if (compose) {
        setDraft({
          ...EMPTY_DRAFT,
          to:
            searchParams.get(
              "to"
            ) || "",
          prospectId:
            searchParams.get(
              "prospectId"
            ) || "",
          subject:
            searchParams.get(
              "subject"
            ) || "",
          body:
            searchParams.get(
              "body"
            ) || "",
        });

        setComposeOpen(
          true
        );
      } else {
        setComposeOpen(
          false
        );
      }
    },
    [searchParams]
  );

  useEffect(
    () => {
      setFolderCreateOpen(
        searchParams.get(
          "createFolder"
        ) === "1"
      );
    },
    [searchParams]
  );


  useEffect(
    () => {
      setAddressBookOpen(
        searchParams.get(
          "addressBook"
        ) === "1"
      );
    },
    [searchParams]
  );

  const checkRecipient =
    useCallback(
      async (
        email = draft.to
      ) => {
        const clean = String(
          email || ""
        )
          .split(/[;,]/)[0]
          .trim();

        if (
          !clean
          || !clean.includes("@")
        ) {
          return;
        }

        try {
          const candidate =
            await mailApi
              .contactCandidate(
                clean
              );

          if (
            candidate.exists
          ) {
            setDraft(
              (current) => ({
                ...current,
                prospectId:
                  candidate
                    .prospect
                    ?.id
                  || current
                    .prospectId,
              })
            );

            await mailApi
              .relinkContact(
                clean
              );

            return;
          }

          if (
            !candidate
              .suppressed
          ) {
            setUnknownContact({
              email: clean,
            });
          }
        } catch {
          // Le mail reste
          // utilisable même
          // si le CRM est
          // indisponible.
        }
      },
      [draft.to]
    );

  async function openMessage(
    item
  ) {
    try {
      const detail =
        await mailApi
          .getMessage(
            item.id
          );

      setSelected(
        detail
      );

      if (
        !detail.isRead
      ) {
        await mailApi
          .updateMessage(
            detail.id,
            {
              is_read:
                true,
            }
          );
      }

      const externalEmail =
        detail.direction
          === "inbound"
          ? detail.fromEmail
          : detail.to?.[0]
            ?.email;

      if (
        !detail.prospectId
        && externalEmail
      ) {
        await checkRecipient(
          externalEmail
        );
      }
    } catch (error) {
      toast.error(
        error?.response
          ?.data?.detail
        || (
          "Impossible "
          + "d'ouvrir le mail"
        )
      );
    }
  }

  function startReply(
    replyAll = false
  ) {
    if (!selected) return;

    const to =
      selected.direction
        === "inbound"
        ? selected.fromEmail
        : (
          selected.to?.[0]
            ?.email
          || ""
        );

    const currentEmail = String(
      accountState.account?.email
      || ""
    )
      .trim()
      .toLowerCase();

    const primaryEmail = String(
      to || ""
    )
      .trim()
      .toLowerCase();

    const extraCc = replyAll
      ? Array.from(
        new Map(
          [
            ...(selected.to || []),
            ...(selected.cc || []),
          ]
            .map((entry) =>
              String(
                entry?.email || ""
              ).trim()
            )
            .filter(Boolean)
            .filter((value) => {
              const normalized =
                value.toLowerCase();

              return (
                normalized
                  !== currentEmail
                && normalized
                  !== primaryEmail
              );
            })
            .map((value) => [
              value.toLowerCase(),
              value,
            ])
        ).values()
      ).join(", ")
      : "";

    setDraft({
      ...EMPTY_DRAFT,
      to,
      cc: extraCc,
      subject:
        /^re:/i.test(
          selected.subject
          || ""
        )
          ? selected.subject
          : (
            "Re: "
            + (
              selected.subject
              || ""
            )
          ),
      prospectId:
        selected.prospectId
        || "",
      inReplyTo:
        selected
          .providerMessageId
        || "",
      references: [
        selected.references
        || "",
        selected
          .providerMessageId
        || "",
      ]
        .filter(Boolean)
        .join(" "),
      body:
        "\n\n--- Message "
        + "précédent ---\n"
        + (
          selected.bodyText
          || ""
        ),
    });

    setComposeOpen(
      true
    );
  }

  function composeToContact(
    contact
  ) {
    if (!contact?.email) {
      return;
    }

    setDraft({
      ...EMPTY_DRAFT,
      to: contact.email,
      prospectId:
        contact.id || "",
    });

    setAddressBookOpen(false);
    setSelected(null);

    const params =
      new URLSearchParams(
        searchParams
      );

    params.delete(
      "addressBook"
    );
    params.set(
      "compose",
      "1"
    );
    params.set(
      "to",
      contact.email
    );
    params.set(
      "prospectId",
      contact.id || ""
    );

    setSearchParams(
      params
    );
  }


  function startForward() {
    if (!selected) return;

    setDraft({
      ...EMPTY_DRAFT,
      subject:
        /^tr:/i.test(
          selected.subject
          || ""
        )
          ? selected.subject
          : (
            "Tr: "
            + (
              selected.subject
              || ""
            )
          ),
      body:
        "\n\n--- Message "
        + "transféré ---\n"
        + "De: "
        + (
          selected.fromEmail
          || ""
        )
        + "\nObjet: "
        + (
          selected.subject
          || ""
        )
        + "\n\n"
        + (
          selected.bodyText
          || ""
        ),
    });

    setComposeOpen(
      true
    );
  }

  function openContactAssociation() {
    if (
      !selected
      || selected.prospectId
      || !canAssociateContact
    ) {
      return;
    }

    setContactAssociationOpen(
      true
    );
  }

  async function associateSelectedContact(
    contact
  ) {
    if (
      !selected
      || selected.prospectId
      || !canAssociateContact
      || !contact?.email
    ) {
      return;
    }

    setContactAssociationSaving(
      true
    );

    try {
      const result =
        await mailApi.associateContact(
          selected.id,
          contact.email
        );

      setSelected(
        (current) => (
          current?.id === selected.id
            ? {
              ...current,
              prospectId:
                result.prospectId,
            }
            : current
        )
      );

      setMessages(
        (current) =>
          current.map(
            (item) => (
              item.id === selected.id
                ? {
                  ...item,
                  prospectId:
                    result.prospectId,
                }
                : item
            )
          )
      );

      setContactAssociationOpen(
        false
      );

      toast.success(
        "Message associé au contact"
      );
    } catch (error) {
      toast.error(
        error?.response
          ?.data?.detail
        || "Association au contact impossible"
      );
    } finally {
      setContactAssociationSaving(
        false
      );
    }
  }

  async function sendMessage(
    event,
    scheduledAt = ""
  ) {
    event?.preventDefault?.();

    if (
      !draft.to.trim()
    ) {
      toast.error(
        "Renseignez un destinataire"
      );
      return;
    }

    // Identical validation for immediate and scheduled sends.
    if (!String(draft.subject || "").trim()) {
      toast.error("Renseignez l’objet du mail");
      return;
    }
    if (!String(draft.body || "").trim()) {
      toast.error("Rédigez le contenu du mail");
      return;
    }

    const selectedFiles = draft.files || [];
    if (
      selectedFiles.some(
        (file) => !file || !Number.isFinite(file.size) || file.size <= 0
      )
    ) {
      toast.error("Une pièce jointe est vide ou indisponible");
      return;
    }
    if (
      selectedFiles.reduce((total, file) => total + file.size, 0)
      > 25 * 1024 * 1024
    ) {
      toast.error("Les pièces jointes dépassent 25 Mo");
      return;
    }

    const requestField = scheduledAt
      ? "scheduleRequestId"
      : "sendRequestId";
    const sendRequestId =
      draft[requestField]
      || window.crypto.randomUUID();

    if (!draft[requestField]) {
      setDraft(
        (current) => ({
          ...current,
          [requestField]: sendRequestId,
        })
      );
    }

    setSending(true);

    try {
      const form =
        new FormData();

      form.append(
        "to",
        draft.to
      );

      form.append(
        "cc",
        draft.cc
      );

      form.append(
        "bcc",
        draft.bcc
      );

      form.append(
        "subject",
        draft.subject
      );

      form.append(
        "text",
        draft.body
      );

      form.append(
        "html",
        ""
      );

      form.append(
        "prospect_id",
        draft.prospectId
        || ""
      );

      form.append(
        "in_reply_to",
        draft.inReplyTo
        || ""
      );

      form.append(
        "references",
        draft.references
        || ""
      );

      form.append(
        "send_request_id",
        sendRequestId
      );

      for (
        const file
        of draft.files
        || []
      ) {
        form.append(
          "files",
          file
        );
      }

      if (scheduledAt) {
        const targetDate = new Date(scheduledAt);
        if (Number.isNaN(targetDate.getTime())) {
          toast.error("Date de planification invalide");
          return;
        }
        form.append("scheduled_at", targetDate.toISOString());
        await mailApi.schedule(form);
        toast.success("Mail planifié");
        setComposeOpen(false);
        setDraft(EMPTY_DRAFT);
        setSearchParams({ folder: "scheduled" });
        return;
      }

      await mailApi.send(form);

      toast.success(
        "Mail envoyé"
      );

      setComposeOpen(
        false
      );

      setDraft(
        EMPTY_DRAFT
      );

      setSearchParams({
        folder: "sent",
      });
    } catch (error) {
      toast.error(
        error?.response
          ?.data?.detail
        || (
          scheduledAt
            ? "Planification impossible"
            : "Envoi du mail impossible"
        )
      );
    } finally {
      setSending(false);
    }
  }

  async function
    suppressUnknownPrompt() {
      if (
        !unknownContact
          ?.email
      ) {
        return;
      }

      try {
        await mailApi
          .saveContactPrompt(
            unknownContact.email,
            "never"
          );
      } finally {
        setUnknownContact(
          null
        );
      }
    }

  function createUnknownContact() {
    if (
      !unknownContact
        ?.email
    ) {
      return;
    }

    window
      .sessionStorage
      .setItem(
        "acta-mail-draft",
        JSON.stringify({
          ...draft,
          files: [],
        })
      );

    const returnTo =
      "/app/mail?"
      + "restoreDraft=1";

    navigate(
      "/app/commerce/"
      + "prospects?"
      + "action=new-persona"
      + "&email="
      + encodeURIComponent(
        unknownContact.email
      )
      + "&returnTo="
      + encodeURIComponent(
        returnTo
      )
    );
  }

  function closeFolderCreate() {
    if (folderCreating) return;

    const params =
      new URLSearchParams(
        searchParams
      );

    params.delete(
      "createFolder"
    );
    setFolderCreateOpen(false);
    setSearchParams(params);
  }

  async function createCustomFolder(
    name
  ) {
    if (
      !name?.trim()
      || folderCreating
    ) {
      return;
    }

    setFolderCreating(true);

    try {
      const created =
        await mailApi.createFolder(
          name.trim()
        );

      await loadFolders();
      window.dispatchEvent(
        new CustomEvent(
          "acta:mail-folders-updated"
        )
      );
      setFolderCreateOpen(false);

      setFolder(
        "custom",
        created.id
      );

      toast.success(
        "Dossier créé"
      );
    } catch (error) {
      toast.error(
        error?.response
          ?.data?.detail
        || "Impossible de créer le dossier"
      );
    } finally {
      setFolderCreating(false);
    }
  }

  function closeFolderRename() {
    if (folderRenaming) return;

    setFolderRenameOpen(false);
  }

  async function renameCustomFolder(
    name
  ) {
    const customFolder =
      mailFolders.find(
        (item) =>
          item.id === folderId
      );

    if (
      !customFolder
      || !name?.trim()
      || folderRenaming
      || name.trim()
        === customFolder.name
    ) {
      return;
    }

    setFolderRenaming(true);

    try {
      await mailApi.renameFolder(
        customFolder.id,
        name.trim()
      );

      await loadFolders();
      window.dispatchEvent(
        new CustomEvent(
          "acta:mail-folders-updated"
        )
      );
      setFolderRenameOpen(false);

      toast.success(
        "Dossier renommé"
      );
    } catch (error) {
      toast.error(
        error?.response
          ?.data?.detail
        || "Impossible de renommer le dossier"
      );
    } finally {
      setFolderRenaming(false);
    }
  }

  async function removeCustomFolder() {
    if (
      !activeCustomFolder
      || folderDeleting
    ) {
      return;
    }

    setFolderDeleting(true);

    try {
      await mailApi.deleteFolder(
        activeCustomFolder.id
      );

      await loadFolders();
      window.dispatchEvent(
        new CustomEvent(
          "acta:mail-folders-updated"
        )
      );
      setFolderDeleteConfirmOpen(
        false
      );
      setFolder("inbox");

      toast.success(
        "Dossier supprimé"
      );
    } catch (error) {
      toast.error(
        error?.response
          ?.data?.detail
        || "Impossible de supprimer le dossier"
      );
    } finally {
      setFolderDeleting(false);
    }
  }

  async function moveSelectedToFolder(
    customFolder
  ) {
    if (!selected) return;

    try {
      await mailApi.moveMessage(
        selected.id,
        "custom",
        customFolder.id
      );

      setSelected(null);
      await loadMessages();

      toast.success(
        `Mail déplacé vers ${customFolder.name}`
      );
    } catch (error) {
      toast.error(
        error?.response
          ?.data?.detail
        || "Impossible de déplacer le mail"
      );
    }
  }

  async function archiveSelected() {
    if (!selected || archiving) return;

    setArchiving(true);

    try {
      await mailApi.moveMessage(
        selected.id,
        "archive"
      );
      setSelected(null);
      await loadMessages();
      toast.success("Message archivé");
    } catch (error) {
      toast.error(
        error?.response
          ?.data?.detail
        || "Impossible d’archiver le message"
      );
    } finally {
      setArchiving(false);
    }
  }

  async function markSelectedAsSpam() {
    if (!selected) return;

    await mailApi.moveMessage(
      selected.id,
      "spam"
    );
    setSelected(null);
    await loadMessages();
  }

  async function restoreSelected() {
    if (!selected || restoring) return;

    setRestoring(true);

    try {
      await mailApi.moveMessage(
        selected.id,
        "inbox"
      );
      setSelected(null);
      await loadMessages();
      toast.success(
        "Message restauré dans la boîte de réception"
      );
    } catch (error) {
      toast.error(
        error?.response
          ?.data?.detail
        || "Impossible de restaurer le message"
      );
    } finally {
      setRestoring(false);
    }
  }

  async function toggleSelectedStar() {
    if (!selected) return;

    const updated =
      await mailApi.updateMessage(
        selected.id,
        {
          is_starred:
            !selected.isStarred,
        }
      );

    setSelected(updated);
  }

  async function markSelectedUnread() {
    if (!selected) return;

    await mailApi.updateMessage(
      selected.id,
      {
        is_read: false,
      }
    );
    setSelected(null);
    await loadMessages();
  }

  async function deleteSelected() {
    if (!selected) return;

    if (folder === "trash") {
      setDeleteConfirmOpen(true);
      return;
    }

    await mailApi.trashMessage(
      selected.id
    );
    setSelected(null);
    await loadMessages();
  }

  async function confirmPermanentDelete() {
    if (
      !selected
      || deletingPermanently
    ) {
      return;
    }

    setDeletingPermanently(true);

    try {
      await mailApi
        .deleteMessagePermanently(
          selected.id
        );
      setDeleteConfirmOpen(false);
      setSelected(null);
      await loadMessages();
      toast.success(
        "Message supprimé définitivement"
      );
    } catch (error) {
      toast.error(
        error?.response
          ?.data?.detail
        || (
          "Impossible de supprimer "
          + "définitivement le message"
        )
      );
    } finally {
      setDeletingPermanently(false);
    }
  }

  function runMobileAction(action) {
    setMobileActionsOpen(false);
    return action();
  }

  const activeCustomFolder =
    useMemo(
      () => (
        folder === "custom"
          ? mailFolders.find(
            (item) =>
              item.id === folderId
          ) || null
          : null
      ),
      [
        folder,
        folderId,
        mailFolders,
      ]
    );

  const folderTitle =
    useMemo(
      () => (
        {
          inbox:
            "Boîte de réception",
          sent:
            "Envoyés",
          starred:
            "Favoris",
          archive:
            "Archives",
          spam:
            "Spam",
          trash:
            "Corbeille",
        }[folder]
        || (
          folder === "custom"
            ? (
              mailFolders.find(
                (item) =>
                  item.id ===
                  folderId
              )?.name
              || "Dossier"
            )
            : "Messagerie"
        )
      ),
      [
        folder,
        folderId,
        mailFolders,
      ]
    );

  if (
    accountState.loading
  ) {
    return (
      <div className="p-6 label-eyebrow lirah-pulse">
        Chargement
      </div>
    );
  }

  return (
    <div
      className="acta-mail-responsive-page min-h-0 min-w-0 w-full lg:h-full lg:overflow-hidden"
      data-testid="mail-page"
    >
      {!accountState
        .configured
        ? (
          <Card variant="acta" className="p-6">
            <h2 className="font-heading text-lg font-black text-[var(--do-ink)]">
              Connecter votre
              boîte professionnelle
            </h2>

            <p className="mt-2 max-w-2xl text-sm text-[var(--do-muted)]">
              Votre administrateur configure
              les paramètres techniques de la
              boîte. Vous gérez votre mot de
              passe depuis Paramètres → Ma
              messagerie.
            </p>

            <Button
              type="button"
              variant="actaPrimary"
              className="mt-4"
              onClick={() =>
                navigate(
                  "/app/settings?section=mail_account"
                )
              }
            >
              Ouvrir les paramètres
            </Button>
          </Card>
        )
        : (
          <Card
            variant="acta"
            data-testid="mail-workspace-card"
            data-mail-view={composeOpen ? "compose" : selected ? "message" : "list"}
            className={[
              "acta-mail-workspace grid grid-cols-[minmax(0,1fr)] min-h-0 min-w-0 w-full overflow-hidden rounded-none border-0 bg-transparent shadow-none lg:h-full lg:rounded-[16px] lg:border lg:border-[var(--do-border)] lg:bg-[var(--do-bg-solid)]",
              composeOpen
                ? "grid-cols-1"
                : "lg:grid-cols-[400px_minmax(0,1fr)]",
            ].join(" ")}
          >
            <section
              className={[
                "acta-mail-list-pane flex min-h-0 min-w-0 flex-col border-b border-[var(--do-border)] lg:border-b-0 lg:border-r",
                composeOpen
                  ? "hidden"
                  : selected
                    ? "hidden lg:flex"
                    : "",
              ].join(" ")}
            >
              <div className="border-b border-[var(--do-border)] bg-[var(--do-bg)] px-3 py-4 lg:bg-transparent lg:p-4" data-testid="mail-list-controls">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-heading text-[14px] font-black tracking-[0.02em] text-[var(--do-ink)]">
                      {folderTitle}
                    </div>

                    {folder === "inbox"
                      && lastSyncAt
                      && (
                        <div className="mt-0.5 text-xs text-[var(--do-muted)]">
                          Dernière synchronisation :{" "}
                          {formatDate(
                            lastSyncAt
                          )}
                        </div>
                      )}
                  </div>

                  {folder === "custom"
                    && activeCustomFolder
                    && (
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="actaSecondary"
                          onClick={() =>
                            setFolderRenameOpen(true)
                          }
                          data-testid="mail-folder-rename-action"
                        >
                          Renommer
                        </Button>

                        <Button
                          type="button"
                          variant="actaSecondary"
                          size="icon"
                          aria-label="Supprimer le dossier"
                          title="Supprimer le dossier"
                          onClick={() =>
                            setFolderDeleteConfirmOpen(true)
                          }
                          data-testid="mail-folder-delete-action"
                        >
                          <Trash
                            size={18}
                          />
                        </Button>
                      </div>
                    )}

                  {folder === "inbox"
                    && (
                      <Button
                        type="button"
                        variant="actaSecondary"
                        size="icon"
                        disabled={
                          syncing
                        }
                        aria-label="Actualiser la boîte de réception"
                        title="Actualiser"
                        onClick={() =>
                          syncInbox()
                        }
                      >
                        <ArrowClockwise
                          size={18}
                          className={
                            syncing
                              ? "animate-spin"
                              : ""
                          }
                        />
                      </Button>
                    )}
                </div>

                <label className="mt-3 flex items-center gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 shadow-sm focus-within:border-[var(--do-accent)]">
                  <MagnifyingGlass
                    className="shrink-0 text-[var(--do-muted)]"
                    size={16}
                  />

                  <Input
                    value={
                      search
                    }
                    onChange={(
                      event
                    ) => {
                      setPage(1);
                      setSearch(
                        event
                          .target
                          .value
                      );
                    }}
                    placeholder="Rechercher"
                    className="min-h-[40px] min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0"
                  />
                </label>
              </div>

              {syncWarning && (
                <div
                  role="status"
                  className="border-b border-[var(--do-border)] bg-[var(--do-bg-2)] px-4 py-3 text-sm text-[var(--do-ink)]"
                >
                  {syncWarning}
                </div>
              )}

              <div className="min-h-0 min-w-0 flex-1 lg:overflow-y-auto">
                {(loading || syncing) && (
                  <div className="p-4 text-sm text-[var(--do-muted)]">
                    {syncing
                      ? "Synchronisation…"
                      : "Chargement…"}
                  </div>
                )}

                {!loading
                  && messages
                    .length === 0
                  && (
                    <div className="p-6 text-center text-sm text-[var(--do-muted)]">
                      Aucun mail.
                    </div>
                  )}

                {messages.map(
                  (item) => (
                    <button
                      key={
                        item.id
                      }
                      type="button"
                      onClick={() =>
                        openMessage(
                          item
                        )
                      }
                      className={[
                        "relative min-h-[72px] min-w-0 w-[calc(100%-16px)] mx-2 mb-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-left shadow-sm transition hover:bg-[var(--do-bg-2)] lg:min-h-[76px] lg:w-full lg:mx-0 lg:mb-0 lg:rounded-none lg:border-0 lg:border-b lg:py-3 lg:pl-4 lg:pr-3 lg:shadow-none",
                        selected?.id
                          === item.id
                          ? "!bg-[var(--do-bg-2)] before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-[var(--do-accent)]"
                          : "",
                        !item.isRead
                          && folder
                            === "inbox"
                          ? "!bg-[var(--do-bg-2)] font-black"
                          : "",
                      ].join(" ")}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                          {!item.isRead
                            && folder === "inbox"
                            && (
                              <span
                                aria-label="Message non lu"
                                className="h-2 w-2 shrink-0 rounded-full bg-[var(--do-accent)]"
                              />
                            )}

                          <span className="truncate text-sm font-bold text-[var(--do-ink)]" title={senderLabel(item)}>
                            {senderLabel(
                              item
                            )}
                          </span>
                        </span>

                        <span className="shrink-0 text-[11px] text-[var(--do-muted)] lg:hidden">
                          {formatDate(
                            item.date
                          )}
                        </span>
                      </div>

                      <div className="mt-1 truncate text-[13px] text-[var(--do-ink-soft)]" title={item.subject || "(sans objet)"}>
                        {item.subject
                          || "(sans objet)"}
                      </div>

                      <div className="mt-1 hidden truncate text-xs font-normal text-[var(--do-muted)] lg:block">
                        {item.bodyText
                          || ""}
                      </div>
                    </button>
                  )
                )}

                {!loading
                  && pageState.totalPages > 1
                  && (
                    <div className="flex items-center justify-between gap-3 border-t border-[var(--do-border)] p-3">
                      <Button
                        type="button"
                        variant="actaSecondary"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage((current) => Math.max(1, current - 1))}
                      >
                        Précédent
                      </Button>

                      <span className="text-xs font-bold text-[var(--do-muted)]">
                        Page {page} / {pageState.totalPages}
                      </span>

                      <Button
                        type="button"
                        variant="actaSecondary"
                        size="sm"
                        disabled={page >= pageState.totalPages}
                        onClick={() => setPage((current) => Math.min(pageState.totalPages, current + 1))}
                      >
                        Suivant
                      </Button>
                    </div>
                  )}
              </div>
            </section>

            <main
              className={[
                "acta-mail-reader-pane min-h-0 min-w-0 lg:overflow-y-auto bg-[var(--do-bg-solid)] lg:bg-transparent",
                !selected && !composeOpen ? "hidden lg:block" : "",
              ].join(" ")}
            >
              {composeOpen
                ? (
                  <MailComposerForm
                    draft={draft}
                    setDraft={setDraft}
                    sending={sending}
                    onClose={() => {
                      setComposeOpen(
                        false
                      );

                      const params =
                        new URLSearchParams(
                          searchParams
                        );

                      params.delete(
                        "compose"
                      );

                      params.delete(
                        "to"
                      );

                      params.delete(
                        "prospectId"
                      );

                      setSearchParams(
                        params
                      );
                    }}
                    onSubmit={sendMessage}
                    onSchedule={(date) => sendMessage(null, date)}
                    onCheckRecipient={
                      checkRecipient
                    }
                    onSearchRecipients={
                      mailApi.recipientSuggestions
                    }
                  />
                )
                : !selected
                  ? (
                    <div className="flex h-full min-h-[45vh] items-center justify-center p-6 text-center text-sm text-[var(--do-muted)]">
                      Sélectionnez
                      un message.
                    </div>
                  )
                  : (
                  <div className="flex min-h-full flex-col">
                    <div
                      className="acta-mail-compact-toolbar sticky top-0 z-10 flex items-center justify-between border-b border-[var(--do-border)] bg-[var(--do-bg-solid)] p-2 lg:hidden"
                      aria-label="Navigation du message mobile"
                      data-testid="mail-mobile-message-toolbar"
                    >
                      <Button
                        type="button"
                        variant="actaGhost"
                        size="icon"
                        className="min-h-[44px] min-w-[44px]"
                        aria-label="Retour à la boîte de réception"
                        onClick={() => {
                          setMobileActionsOpen(false);
                          setSelected(null);

                          const params =
                            new URLSearchParams(searchParams);

                          params.delete("message");
                          setSearchParams(params);
                        }}
                      >
                        <ArrowBendUpLeft size={20} />
                      </Button>

                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="actaGhost"
                        size="icon"
                        className="min-h-[44px] min-w-[44px]"
                          title={selected.isStarred ? "Retirer des favoris" : "Ajouter aux favoris"}
                          aria-label={selected.isStarred ? "Retirer des favoris" : "Ajouter aux favoris"}
                          onClick={toggleSelectedStar}
                        >
                          <Star
                            size={20}
                            weight={selected.isStarred ? "fill" : "regular"}
                          />
                        </Button>

                        <Button
                          type="button"
                          variant="actaGhost"
                        size="icon"
                        className="min-h-[44px] min-w-[44px]"
                          aria-label="Plus d’actions"
                          aria-expanded={mobileActionsOpen}
                          onClick={() => setMobileActionsOpen(true)}
                          data-testid="mail-mobile-actions-trigger"
                        >
                          <DotsThree size={24} weight="bold" />
                        </Button>
                      </div>
                    </div>

                    <div
                      className="acta-mail-wide-toolbar sticky top-0 z-10 hidden items-center justify-between gap-3 border-b border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3 sm:px-5 lg:flex"
                      aria-label="Actions du message"
                      data-testid="mail-desktop-message-toolbar"
                    >
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          onClick={() => startReply(false)}
                          variant="actaSecondary"
                          className="min-h-[44px]"
                        >
                          <ArrowBendUpLeft size={18} />
                          Répondre
                        </Button>

                        <Button
                          type="button"
                          onClick={() => startReply(true)}
                          variant="actaSecondary"
                          className="min-h-[44px]"
                        >
                          <EnvelopeOpen size={18} />
                          Répondre à tous
                        </Button>

                        <Button
                          type="button"
                          onClick={startForward}
                          variant="actaSecondary"
                          className="min-h-[44px]"
                        >
                          <ArrowBendUpRight size={18} />
                          Transférer
                        </Button>

                        {canAssociateContact && !selected.prospectId && (
                          <Button
                            type="button"
                            onClick={openContactAssociation}
                            variant="actaSecondary"
                          className="min-h-[44px]"
                            data-testid="mail-associate-contact"
                          >
                            <UserPlus size={18} />
                            Associer à un contact
                          </Button>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        {visibleMessageActions.length > 0 && (
                          <>
                            <Button
                              ref={messageActionsTriggerRef}
                              type="button"
                              title="Associations"
                              aria-label="Associations du message"
                              aria-expanded={messageActionsOpen}
                              onClick={() => setMessageActionsOpen((open) => !open)}
                              variant="actaSecondary"
                          size="icon"
                          className="min-h-[44px] min-w-[44px]"
                              data-testid="mail-desktop-association-actions-trigger"
                            >
                              <DotsThree size={20} weight="bold" />
                            </Button>

                            <ActaActionMenu
                              open={messageActionsOpen}
                              anchorEl={messageActionsTriggerRef.current}
                              onClose={() => setMessageActionsOpen(false)}
                              testId="mail-desktop-association-actions-menu"
                            >
                              {visibleMessageActions.map((action) => (
                                <ActaActionMenuItem
                                  key={action.testId || action.label}
                                  icon={action.icon}
                                  label={action.label}
                                  onClick={() => {
                                    setMessageActionsOpen(false);
                                    action.onClick();
                                  }}
                                  testId={action.testId}
                                />
                              ))}
                            </ActaActionMenu>
                          </>
                        )}

                        {folder !== "archive" && (
                          <Button
                            type="button"
                            title="Archiver"
                            aria-label="Archiver"
                            onClick={archiveSelected}
                            disabled={archiving}
                            variant="actaSecondary"
                          size="icon"
                          className="min-h-[44px] min-w-[44px]"
                            data-testid="mail-archive-message"
                          >
                            <Archive size={18} />
                          </Button>
                        )}

                        {folder !== "spam" && (
                          <Button
                            type="button"
                            title="Signaler comme spam"
                            aria-label="Signaler comme spam"
                            onClick={markSelectedAsSpam}
                            variant="actaSecondary"
                          size="icon"
                          className="min-h-[44px] min-w-[44px]"
                          >
                            <Warning size={18} />
                          </Button>
                        )}

                        {(folder === "trash"
                          || folder === "archive"
                          || folder === "spam") && (
                          <Button
                            type="button"
                            title="Restaurer dans la boîte de réception"
                            aria-label="Restaurer dans la boîte de réception"
                            onClick={restoreSelected}
                            disabled={restoring}
                            variant="actaSecondary"
                            className="min-h-[44px]"
                            data-testid="mail-restore-message"
                          >
                            {restoring ? "Restauration…" : "Restaurer"}
                          </Button>
                        )}

                        <Button
                          type="button"
                          title={selected.isStarred ? "Retirer des favoris" : "Ajouter aux favoris"}
                          aria-label={selected.isStarred ? "Retirer des favoris" : "Ajouter aux favoris"}
                          onClick={toggleSelectedStar}
                          variant="actaSecondary"
                          size="icon"
                          className="min-h-[44px] min-w-[44px]"
                        >
                          <Star
                            size={18}
                            weight={selected.isStarred ? "fill" : "regular"}
                          />
                        </Button>

                        {mailFolders.length > 0 && (
                          <label className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-[var(--do-border)] px-3">
                            <Folder size={18} />
                            <span className="sr-only">Déplacer vers</span>
                            <select
                              aria-label="Déplacer le message vers"
                              defaultValue=""
                              onChange={(event) => {
                                const target = mailFolders.find(
                                  (item) => item.id === event.target.value
                                );

                                if (target) {
                                  moveSelectedToFolder(target);
                                }

                                event.target.value = "";
                              }}
                              className="max-w-[170px] bg-transparent text-sm font-bold text-[var(--do-ink)] outline-none"
                            >
                              <option value="">Déplacer vers…</option>
                              {mailFolders.map((customFolder) => (
                                <option
                                  key={customFolder.id}
                                  value={customFolder.id}
                                >
                                  {customFolder.name}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}

                        <Button
                          type="button"
                          title={folder === "trash" ? "Supprimer définitivement" : "Déplacer vers la corbeille"}
                          aria-label={folder === "trash" ? "Supprimer définitivement" : "Déplacer vers la corbeille"}
                          onClick={deleteSelected}
                          disabled={
                            folder === "trash"
                            && deletingPermanently
                          }
                          variant="actaSecondary"
                          size="icon"
                          className="min-h-[44px] min-w-[44px]"
                          data-testid={
                            folder === "trash"
                              ? "mail-delete-permanently"
                              : "mail-trash-message"
                          }
                        >
                          <Trash size={18} />
                        </Button>
                      </div>
                    </div>

                    <article className="min-w-0 flex-1 p-4">
                      <div
                        className="acta-mail-compact-heading flex min-w-0 items-center gap-2 border-b border-[var(--do-border)] pb-2 lg:hidden"
                        data-testid="mail-mobile-message-heading"
                      >
                        <h2 className="min-w-0 flex-[1.4] truncate text-sm font-bold text-[var(--do-ink)]" title={selected.subject || "(sans objet)"}>
                          {selected.subject || "(sans objet)"}
                        </h2>
                        <span className="min-w-0 flex-1 truncate text-xs text-[var(--do-muted)]" title={selected.fromName || selected.fromEmail || "—"}>
                          {selected.fromName || selected.fromEmail || "—"}
                        </span>
                        <time className="shrink-0 whitespace-nowrap text-[10px] text-[var(--do-muted)]" title={formatDate(selected.date)}>
                          {formatDate(selected.date)}
                        </time>
                      </div>

                      <div className="acta-mail-wide-heading hidden border-b border-[var(--do-border)] pb-4 lg:block" data-testid="mail-desktop-message-heading">
                        <h2 className="break-words font-heading text-xl font-black text-[var(--do-ink)]">
                          {selected.subject || "(sans objet)"}
                        </h2>

                        <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                          <p className="min-w-0 break-all text-sm text-[var(--do-muted)]">
                            De : {selected.fromName || selected.fromEmail || "—"}
                          </p>

                          <p className="shrink-0 text-xs text-[var(--do-muted)]">
                            {formatDate(selected.date)}
                          </p>
                        </div>
                      </div>

                      <div className="whitespace-pre-wrap break-words py-5 text-sm leading-7 text-[var(--do-ink)]">
                        {selected.bodyText || "Aucun contenu texte disponible."}
                      </div>

                      {(selected.attachments || []).length > 0 && (
                        <section className="border-t border-[var(--do-border)] py-4">
                          <div className="text-xs font-black uppercase tracking-wide text-[var(--do-muted)]">
                            Pièces jointes
                          </div>

                          <div className="mt-2 flex flex-wrap gap-2">
                            {selected.attachments.map((attachment) => (
                              <div
                                key={attachment.id}
                                className="flex max-w-full items-center overflow-hidden rounded-xl border border-[var(--do-border)]"
                              >
                                <a
                                  href={attachment.downloadUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="max-w-[260px] truncate px-3 py-2 text-sm font-bold text-[var(--do-accent)]"
                                  title={`Consulter ${attachment.filename}`}
                                >
                                  {attachment.filename}
                                </a>

                                <a
                                  href={`${attachment.downloadUrl}/download`}
                                  className="border-l border-[var(--do-border)] px-3 py-2 text-xs font-black text-[var(--do-ink)]"
                                  title={`Télécharger ${attachment.filename}`}
                                  aria-label={`Télécharger ${attachment.filename}`}
                                >
                                  Télécharger
                                </a>

                                {selected.direction === "inbound"
                                  && canSaveAttachmentsToDocuments
                                  && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setAttachmentSaveTarget(
                                          attachment
                                        )
                                      }
                                      className="border-l border-[var(--do-border)] px-3 py-2 text-xs font-black text-[var(--do-ink)] hover:bg-[var(--do-bg-2)]"
                                      title={`Enregistrer ${attachment.filename} dans Documents`}
                                      aria-label={`Enregistrer ${attachment.filename} dans Documents`}
                                      data-testid="mail-save-attachment-to-documents"
                                    >
                                      Enregistrer dans Documents
                                    </button>
                                  )}
                              </div>
                            ))}
                          </div>
                        </section>
                      )}
                    </article>

                    <div className="sticky bottom-0 border-t border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3 lg:hidden">
                      <Button
                        type="button"
                        variant="actaPrimary"
                        className="min-h-[44px] w-full"
                        onClick={() => startReply(false)}
                        data-testid="mail-mobile-reply"
                      >
                        <ArrowBendUpLeft size={18} />
                        Répondre
                      </Button>
                    </div>

                    <ActaActionSheet
                      open={mobileActionsOpen}
                      title="Actions du message"
                      subtitle={selected.subject || "(sans objet)"}
                      onClose={() => setMobileActionsOpen(false)}
                      testId="mail-mobile-actions-sheet"
                    >
                      <ActaActionMenuItem
                        icon={<EnvelopeOpen size={20} />}
                        label="Répondre à tous"
                        onClick={() => runMobileAction(() => startReply(true))}
                      />
                      <ActaActionMenuItem
                        icon={<ArrowBendUpRight size={20} />}
                        label="Transférer"
                        onClick={() => runMobileAction(startForward)}
                      />

                      {canAssociateContact && !selected.prospectId && (
                        <ActaActionMenuItem
                          icon={<UserPlus size={20} />}
                          label="Associer à un contact"
                          onClick={() => runMobileAction(openContactAssociation)}
                          testId="mail-mobile-associate-contact"
                        />
                      )}

                      {visibleMessageActions.map((action) => (
                        <ActaActionMenuItem
                          key={action.testId || action.label}
                          icon={action.icon}
                          label={action.label}
                          onClick={() => runMobileAction(action.onClick)}
                          testId={action.testId}
                        />
                      ))}

                      {folder !== "archive" && (
                        <ActaActionMenuItem
                          icon={<Archive size={20} />}
                          label={archiving ? "Archivage…" : "Archiver"}
                          onClick={() => runMobileAction(archiveSelected)}
                          testId="mail-mobile-archive-message"
                        />
                      )}

                      <ActaActionMenuItem
                        icon={<EnvelopeSimple size={20} />}
                        label="Marquer comme non lu"
                        onClick={() => runMobileAction(markSelectedUnread)}
                      />

                      {mailFolders.length > 0 && (
                        <label className="acta-action-menu-item">
                          <Folder size={20} />
                          <span className="acta-action-menu-item-copy">
                            <strong>Déplacer vers</strong>
                            <select
                              aria-label="Déplacer le message vers depuis le menu mobile"
                              defaultValue=""
                              onChange={(event) => {
                                const target = mailFolders.find(
                                  (item) => item.id === event.target.value
                                );

                                if (target) {
                                  runMobileAction(() => moveSelectedToFolder(target));
                                }

                                event.target.value = "";
                              }}
                              className="mt-1 max-w-full bg-transparent text-sm text-[var(--do-ink)] outline-none"
                            >
                              <option value="">Choisir un dossier…</option>
                              {mailFolders.map((customFolder) => (
                                <option
                                  key={customFolder.id}
                                  value={customFolder.id}
                                >
                                  {customFolder.name}
                                </option>
                              ))}
                            </select>
                          </span>
                        </label>
                      )}

                      {folder !== "spam" && (
                        <ActaActionMenuItem
                          icon={<Warning size={20} />}
                          label="Signaler comme spam"
                          onClick={() => runMobileAction(markSelectedAsSpam)}
                        />
                      )}

                      {(folder === "trash"
                        || folder === "archive"
                        || folder === "spam") && (
                        <ActaActionMenuItem
                          icon={<ArrowBendUpLeft size={20} />}
                          label={
                            restoring
                              ? "Restauration…"
                              : "Restaurer dans la boîte de réception"
                          }
                          onClick={() => runMobileAction(restoreSelected)}
                          testId="mail-mobile-restore-message"
                        />
                      )}

                      <ActaActionMenuItem
                        icon={<Trash size={20} />}
                        label={
                          folder === "trash"
                            ? (
                              deletingPermanently
                                ? "Suppression…"
                                : "Supprimer définitivement"
                            )
                            : "Déplacer vers la corbeille"
                        }
                        danger
                        onClick={() => runMobileAction(deleteSelected)}
                        testId={
                          folder === "trash"
                            ? "mail-mobile-delete-permanently"
                            : "mail-mobile-trash-message"
                        }
                      />
                    </ActaActionSheet>
                  </div>
                )}
            </main>
          </Card>
        )}

      <MailFolderCreateDialog
        open={folderCreateOpen}
        saving={folderCreating}
        onClose={closeFolderCreate}
        onSubmit={createCustomFolder}
      />

      <MailFolderCreateDialog
        open={
          folderRenameOpen
          && Boolean(
            activeCustomFolder
          )
        }
        saving={folderRenaming}
        initialName={
          activeCustomFolder
            ?.name
          || ""
        }
        title="Renommer le dossier"
        description="Choisissez un nouveau nom pour ce dossier personnalisé."
        submitLabel="Renommer"
        savingLabel="Renommage…"
        testIdPrefix="mail-folder-rename"
        requireChange
        onClose={closeFolderRename}
        onSubmit={renameCustomFolder}
      />

      <ActaConfirmDialog
        open={
          folderDeleteConfirmOpen
          && Boolean(
            activeCustomFolder
          )
        }
        eyebrow="Dossier personnalisé"
        title={
          activeCustomFolder
            ? `Supprimer le dossier « ${activeCustomFolder.name} » ?`
            : "Supprimer ce dossier ?"
        }
        description={
          "Les messages qu’il contient seront "
          + "replacés dans la boîte de réception. "
          + "Cette action ne supprime aucun message."
        }
        confirmLabel="Supprimer le dossier"
        saving={folderDeleting}
        savingLabel="Suppression…"
        danger
        onConfirm={removeCustomFolder}
        onClose={() => {
          if (!folderDeleting) {
            setFolderDeleteConfirmOpen(
              false
            );
          }
        }}
      />

      <ActaConfirmDialog
        open={deleteConfirmOpen}
        eyebrow="Suppression définitive"
        title="Supprimer définitivement ce message ?"
        description={
          "Cette action est irréversible. "
          + "Le message et ses pièces jointes "
          + "seront supprimés d’Acta."
        }
        confirmLabel="Supprimer définitivement"
        saving={deletingPermanently}
        savingLabel="Suppression…"
        danger
        onConfirm={confirmPermanentDelete}
        onClose={() => {
          if (!deletingPermanently) {
            setDeleteConfirmOpen(false);
          }
        }}
      />

      <MailAddressBookDialog
        open={addressBookOpen}
        accountEmail={
          accountState
            .account
            ?.email
          || ""
        }
        onClose={() => {
          setAddressBookOpen(false);

          const params =
            new URLSearchParams(
              searchParams
            );

          params.delete(
            "addressBook"
          );

          setSearchParams(
            params
          );
        }}
        onCompose={composeToContact}
      />

      <MailContactAssociationDialog
        open={
          contactAssociationOpen
          && Boolean(selected)
          && !selected?.prospectId
          && canAssociateContact
        }
        suggestedEmail={
          selected?.direction === "inbound"
            ? selected?.fromEmail || ""
            : selected?.to?.[0]?.email || ""
        }
        saving={contactAssociationSaving}
        onClose={() => setContactAssociationOpen(false)}
        onSearchRecipients={mailApi.recipientSuggestions}
        onAssociate={associateSelectedContact}
      />

      <MailAttachmentSaveDialog
        open={
          Boolean(attachmentSaveTarget)
          && Boolean(selected)
          && selected?.direction === "inbound"
          && canSaveAttachmentsToDocuments
        }
        messageId={selected?.id || ""}
        attachment={attachmentSaveTarget}
        onClose={() =>
          setAttachmentSaveTarget(null)
        }
      />

      <UnknownMailContactDialog
        contact={unknownContact}
        onClose={() => setUnknownContact(null)}
        onNeverAsk={suppressUnknownPrompt}
        onCreate={createUnknownContact}
      />
    </div>
  );
}
